import React, { useState, useEffect } from 'react';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useVerification } from '../../hooks/useVerification';
import { CustomerBookingAPI, CustomerBookingErrorCodes } from '../../services/api/customerBookingAPI';
import {
  WelcomeScreen,
  PhoneInputScreen,
  VerificationCodeScreen,
  CustomerRegistrationForm,
  AppointmentsList,
  BookingWizard,
  CancelConfirmationModal,
  CancellationPolicy
} from '../../components/customer/booking';

// ===========================================
// VERIFICATION BYPASS FLAG
// Set to 'true' to skip phone verification
// ===========================================
const SKIP_VERIFICATION = false;

/**
 * CustomerBooking Component - Manages the customer booking flow
 *
 * SCREEN STATES:
 * 1. 'welcome' - Initial welcome screen with business branding and welcome text
 * 2. 'phone' - Phone number input screen where customer enters their phone
 * 3. 'verification' - SMS verification code input screen (skipped if SKIP_VERIFICATION is true)
 * 4. 'registration' - New customer registration form (full name, email, birth date, gender)
 * 5. 'appointments' - List of customer's future appointments with ability to cancel or book new
 * 6. 'booking' - New appointment booking wizard (service selection, date/time picker, etc.)
 *
 * FLOW:
 * - Normal flow: welcome → phone → verification → registration/appointments → booking
 * - Skip verification flow: welcome → phone → registration/appointments → booking
 *
 * CUSTOMER STATES:
 * - New customer (customerExists = false): Goes to 'registration' screen after phone verification
 * - Existing customer (customerExists = true): Goes to 'appointments' screen after phone verification
 * - Unregistered booking disabled: New customers cannot book, only existing customers can proceed
 */
const CustomerBooking = ({ businessContext = null, businessSlug = null }) => {
  // Screen navigation
  const [currentScreen, setCurrentScreen] = useState('welcome');
  // Screens: 'welcome' | 'phone' | 'verification' | 'registration' | 'appointments' | 'booking' | 'cancellationPolicy'

  // Business context
  const [businessLogo, setBusinessLogo] = useState(null);
  const [mainPhoto, setMainPhoto] = useState(null);
  const [welcomeText, setWelcomeText] = useState('');

  /**
   * Translate error codes to Hebrew user-friendly messages
   */
  const getErrorMessage = (error) => {
    if (!error) return 'שגיאה לא ידועה';

    const errorCode = error.errorCode;

    // Map error codes to Hebrew messages
    const errorMessages = {
      [CustomerBookingErrorCodes.PHONE_MISMATCH]: 'מספר הטלפון אינו תואם את מספר האימות',
      [CustomerBookingErrorCodes.UNAUTHORIZED]: 'אין הרשאה לביצוע פעולה זו',
      [CustomerBookingErrorCodes.INVALID_TOKEN]: 'אסימון האימות אינו תקף. אנא התחבר מחדש',
      [CustomerBookingErrorCodes.CUSTOMER_NOT_FOUND]: 'לקוח לא נמצא במערכת',
      [CustomerBookingErrorCodes.UNREGISTERED_BOOKING_DISABLED]: 'הזמנות ללקוחות חדשים אינן מאופשרות כרגע',
      [CustomerBookingErrorCodes.FULL_NAME_REQUIRED]: 'יש להזין שם מלא לצורך הרשמה',
      [CustomerBookingErrorCodes.INVALID_CUSTOMER_DATA]: 'פרטי הלקוח אינם תקינים',
      [CustomerBookingErrorCodes.APPOINTMENT_ID_REQUIRED]: 'מזהה תור חסר',
      [CustomerBookingErrorCodes.APPOINTMENT_DATA_REQUIRED]: 'פרטי התור חסרים',
      [CustomerBookingErrorCodes.SERVICE_ID_REQUIRED]: 'יש לבחור שירות',
      [CustomerBookingErrorCodes.DATE_TIME_REQUIRED]: 'יש לבחור תאריך ושעה',
      [CustomerBookingErrorCodes.BUSINESS_NOT_FOUND]: 'העסק לא נמצא במערכת',
      [CustomerBookingErrorCodes.BUSINESS_CONTEXT_ERROR]: 'שגיאה בטעינת פרטי העסק',
      [CustomerBookingErrorCodes.DATABASE_ERROR]: 'שגיאה בגישה למסד הנתונים. אנא נסו שוב',
      [CustomerBookingErrorCodes.INTERNAL_ERROR]: 'שגיאת שרת פנימית. אנא נסו שוב מאוחר יותר',
      [CustomerBookingErrorCodes.OPERATION_FAILED]: 'הפעולה נכשלה. אנא נסו שוב'
    };

    // Return mapped error message or use error message directly
    if (errorCode && errorMessages[errorCode]) {
      return errorMessages[errorCode];
    }

    // Fallback to error message or generic message
    return error.message || 'אירעה שגיאה. אנא נסו שוב';
  };

  // Customer data
  const [customer, setCustomer] = useState(null);
  const [customerAppointments, setCustomerAppointments] = useState([]);

  // Phone verification
  const {
    verificationCode,
    setVerificationCode,
    error: verificationError,
    initiateVerification,
    verifyCode,
    isVerified,
    verifiedPhoneNumber,
    clearError: clearVerificationError,
    clearVerification
  } = useVerification(businessSlug);

  // Registration flow
  const [customerExists, setCustomerExists] = useState(null);
  const [unregisteredBookingEnabled, setUnregisteredBookingEnabled] = useState(false);

  // Loading states
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [isCheckingCustomer, setIsCheckingCustomer] = useState(false);
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(false);
  const [cancellingAppointmentId, setCancellingAppointmentId] = useState(null);

  // Cancel modal
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);

  // Load business assets
  useEffect(() => {
    const fetchBusinessAssets = async () => {
      if (!businessSlug) return;

      try {
        const storage = getStorage();

        // Fetch business logo
        try {
          const logoRef = ref(storage, `slug-to-logo/${businessSlug}.jpeg`);
          const logoUrl = await getDownloadURL(logoRef);
          setBusinessLogo(logoUrl);
        } catch (error) {
          console.log('Logo not found for business:', businessSlug);
          setBusinessLogo('/clinic-q-logo.png');
        }

        // Fetch main photo
        try {
          const mainPhotoRef = ref(storage, `slug-to-photo/${businessSlug}.jpeg`);
          const mainPhotoUrl = await getDownloadURL(mainPhotoRef);
          setMainPhoto(mainPhotoUrl);
        } catch (error) {
          console.log('Main photo not found for business:', businessSlug);
          setMainPhoto(null);
        }

        // Fetch welcome text from Firestore
        try {
          const db = getFirestore();
          const welcomeTextDoc = await getDoc(doc(db, 'business_welcome_texts', businessSlug));

          if (welcomeTextDoc.exists()) {
            const data = welcomeTextDoc.data();
            setWelcomeText(data.text || data.welcomeText || '');
          } else {
            console.log('Welcome text not found for business:', businessSlug);
            setWelcomeText('ברוכים הבאים! אנו שמחים לארח אתכם ולספק לכם את השירות הטוב ביותר.');
          }
        } catch (error) {
          console.log('Error fetching welcome text for business:', businessSlug, error);
          setWelcomeText('ברוכים הבאים! אנו שמחים לארח אתכם ולספק לכם את השירות הטוב ביותר.');
        }
      } catch (error) {
        console.error('Error fetching business assets:', error);
      }
    };

    fetchBusinessAssets();
  }, [businessSlug]);

  // Update favicon, page title, and meta tags when business logo loads
  useEffect(() => {
    if (businessLogo && businessSlug) {
      const pageTitle = `קביעת תור - ${businessSlug}`;
      const pageDescription = welcomeText || `קביעת תור אונליין - ${businessSlug}`;
      const pageUrl = window.location.href;

      // Update favicon
      let faviconLink = document.querySelector("link[rel='icon']");
      if (!faviconLink) {
        faviconLink = document.createElement('link');
        faviconLink.rel = 'icon';
        document.head.appendChild(faviconLink);
      }
      faviconLink.href = businessLogo;

      // Update apple touch icon
      let appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']");
      if (!appleTouchIcon) {
        appleTouchIcon = document.createElement('link');
        appleTouchIcon.rel = 'apple-touch-icon';
        document.head.appendChild(appleTouchIcon);
      }
      appleTouchIcon.href = businessLogo;

      // Update page title
      document.title = pageTitle;

      // Update meta description
      let metaDescription = document.querySelector("meta[name='description']");
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.name = 'description';
        document.head.appendChild(metaDescription);
      }
      metaDescription.content = pageDescription;

      // Open Graph tags for WhatsApp, Facebook, etc.
      const updateMetaTag = (property, content) => {
        let tag = document.querySelector(`meta[property='${property}']`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute('property', property);
          document.head.appendChild(tag);
        }
        tag.setAttribute('content', content);
      };

      updateMetaTag('og:title', pageTitle);
      updateMetaTag('og:description', pageDescription);
      updateMetaTag('og:image', businessLogo);
      updateMetaTag('og:url', pageUrl);
      updateMetaTag('og:type', 'website');

      // Twitter Card tags (also used by some platforms)
      const updateTwitterTag = (name, content) => {
        let tag = document.querySelector(`meta[name='${name}']`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute('name', name);
          document.head.appendChild(tag);
        }
        tag.setAttribute('content', content);
      };

      updateTwitterTag('twitter:card', 'summary_large_image');
      updateTwitterTag('twitter:title', pageTitle);
      updateTwitterTag('twitter:description', pageDescription);
      updateTwitterTag('twitter:image', businessLogo);
    }

    // Cleanup: restore defaults when component unmounts
    return () => {
      document.title = 'Clinic-Q Systems';
      const faviconLink = document.querySelector("link[rel='icon']");
      if (faviconLink) {
        faviconLink.href = '/favicon.ico';
      }
      const appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']");
      if (appleTouchIcon) {
        appleTouchIcon.href = '/android-chrome-192x192.png';
      }
      const metaDescription = document.querySelector("meta[name='description']");
      if (metaDescription) {
        metaDescription.content = 'קליניק-קיו מערכות - כל מה שצריך לניהול הקליניקה במקום אחד';
      }
    };
  }, [businessLogo, welcomeText, businessSlug]);

  // Phone validation
  const validatePhone = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    const normalized = cleaned.startsWith('972') ? '0' + cleaned.slice(3) : cleaned;
    const isValid = /^05\d{8}$/.test(normalized);
    return { isValid, normalized };
  };

  // Verification error messages
  const getVerificationErrorMessage = (error) => {
    if (!error) return '';
    const errorMsg = error.toLowerCase();
    if (errorMsg.includes('invalid') || errorMsg.includes('incorrect')) {
      return 'קוד אימות שגוי. אנא נסו שוב.';
    }
    if (errorMsg.includes('expired')) {
      return 'קוד האימות פג תוקף. אנא בקשו קוד חדש.';
    }
    if (errorMsg.includes('rate limit') || errorMsg.includes('too many')) {
      return 'יותר מדי ניסיונות. אנא נסו שוב מאוחר יותר.';
    }
    return 'שגיאה באימות. אנא נסו שוב.';
  };

  // Handlers
  const handleContinueFromWelcome = () => {
    setCurrentScreen('phone');
  };

  const handleShowCancellationPolicy = () => {
    setCurrentScreen('cancellationPolicy');
  };

  const handleBackFromCancellationPolicy = () => {
    setCurrentScreen('welcome');
  };

  const handlePhoneChange = (e) => {
    setPhoneNumber(e.target.value);
    if (phoneError) setPhoneError('');
  };

  const handlePhoneSubmit = async () => {
    const validation = validatePhone(phoneNumber);

    if (!validation.isValid) {
      setPhoneError('מספר טלפון לא תקין');
      return;
    }

    setPhoneError('');
    setIsCheckingCustomer(true);

    try {
      // Check if verification should be skipped
      if (SKIP_VERIFICATION) {
        // Skip verification completely - lookup customer directly by phone
        // Store normalized phone for use in registration if needed
        setPhoneNumber(validation.normalized);

        try {
          const verificationToken = `skipped_verification_${validation.normalized}_${Date.now()}`;
          const customerData = await CustomerBookingAPI.lookupCustomerByPhone(
            businessSlug,
            validation.normalized,
            verificationToken
          );

          if (customerData) {
            // Existing customer found - go directly to appointments
            setCustomer(customerData);
            setCustomerExists(true);
            setCurrentScreen('appointments');
            loadCustomerAppointments(customerData.token);
          } else {
            // New customer - go directly to registration
            setCustomerExists(false);
            setCurrentScreen('registration');
          }
        } catch (error) {
          // If customer lookup fails with specific error, treat as new customer
          console.log('Customer not found, proceeding to registration:', error);
          setCustomerExists(false);
          setCurrentScreen('registration');
        }
      } else {
        // Normal verification flow - send SMS
        const result = await initiateVerification(validation.normalized);
        if (result.success) {
          setCurrentScreen('verification');
          setCustomerExists(result.customerExists);
          setUnregisteredBookingEnabled(result.unregisteredCustomerBookingEnabled);
        } else {
          if (verificationError) {
            setPhoneError(getVerificationErrorMessage(verificationError));
          } else {
            setPhoneError('נכשל בשליחת קוד אימות. אנא נסו שוב.');
          }
        }
      }
    } catch (error) {
      console.error('Error in phone submit:', error);
      setPhoneError('שגיאה בעיבוד מספר הטלפון. אנא נסו שוב.');
    } finally {
      setIsCheckingCustomer(false);
    }
  };

  const handleVerificationCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
    setVerificationCode(value);
    if (verificationError) clearVerificationError();
  };

  const handleVerifyCode = async () => {
    clearVerificationError();
    setIsCheckingCustomer(true);

    try {
      const result = await verifyCode();
      if (result && result.success) {
        // Check if customer exists
        if (customerExists === false && unregisteredBookingEnabled) {
          // Customer doesn't exist but unregistered booking is allowed
          // Go directly to registration screen WITHOUT calling lookup API
          setIsCheckingCustomer(false);
          setCurrentScreen('registration');
          return;
        }

        // For existing customers OR when unregistered booking is disabled - lookup customer
        try {
          const verificationToken = `verified_${result.phoneNumber}_${Date.now()}`;
          const customerData = await CustomerBookingAPI.lookupCustomerByPhone(
            businessSlug,
            result.phoneNumber,
            verificationToken
          );

          if (customerData) {
            setCustomer(customerData);
            setCurrentScreen('appointments');
            loadCustomerAppointments(customerData.token);
          } else {
            setPhoneError('לקוח עם מספר טלפון זה לא נמצא במערכת');
            setCurrentScreen('phone');
          }
        } catch (error) {
          console.error('Error looking up customer:', error);
          setPhoneError(getErrorMessage(error));
          setCurrentScreen('phone');
        }
      }
    } catch (error) {
      console.error('שגיאה באימות קוד:', error);
    } finally {
      setIsCheckingCustomer(false);
    }
  };

  const handleRegistrationSubmit = async (formData) => {
    setIsCheckingCustomer(true);

    try {
      // Use phone number from either verification or skip verification flow
      const phoneToUse = SKIP_VERIFICATION ? phoneNumber : verifiedPhoneNumber;

      // Use appropriate token prefix based on verification mode
      const verificationToken = SKIP_VERIFICATION
        ? `skipped_verification_${phoneToUse}_${Date.now()}`
        : `verified_${phoneToUse}_${Date.now()}`;

      const customerData = await CustomerBookingAPI.lookupCustomerByPhone(
        businessSlug,
        phoneToUse,
        verificationToken,
        {
          customerFullName: formData.fullName,
          email: formData.email,
          birth_date: formData.birthDate,
          gender: formData.gender
        }
      );

      if (customerData) {
        setCustomer(customerData);
        setCurrentScreen('appointments');
        loadCustomerAppointments(customerData.token);
      }
    } catch (error) {
      console.error('Error creating customer:', error);
      setPhoneError(getErrorMessage(error));
      setCurrentScreen('phone');
    } finally {
      setIsCheckingCustomer(false);
    }
  };

  const loadCustomerAppointments = async (customerToken) => {
    setIsLoadingAppointments(true);
    try {
      const appointments = await CustomerBookingAPI.getCustomerAppointments(businessSlug, customerToken);

      // Filter for future appointments only
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const futureAppointments = appointments.filter((apt) => {
        const appointmentDate = new Date(apt.date);
        return appointmentDate >= today;
      });

      setCustomerAppointments(futureAppointments);
    } catch (error) {
      console.error('Error loading customer appointments:', error);
      // Show error to user if needed
      alert(getErrorMessage(error));
    } finally {
      setIsLoadingAppointments(false);
    }
  };

  const handleCancelAppointment = (appointmentId) => {
    const appointment = customerAppointments.find((apt) => apt.id === appointmentId);
    setAppointmentToCancel(appointment);
    setShowCancelModal(true);
  };

  const confirmCancelAppointment = async () => {
    if (!appointmentToCancel || !customer?.token) return;

    setCancellingAppointmentId(appointmentToCancel.id);

    try {
      await CustomerBookingAPI.cancelAppointment(businessSlug, appointmentToCancel.id, customer.token);

      // Update appointment status to cancelled
      setCustomerAppointments((prev) =>
        prev.map((apt) => (apt.id === appointmentToCancel.id ? { ...apt, status: 'cancelled' } : apt))
      );

      setShowCancelModal(false);
      setAppointmentToCancel(null);
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      alert(getErrorMessage(error));
    } finally {
      setCancellingAppointmentId(null);
    }
  };

  const handleBookNewAppointment = () => {
    setCurrentScreen('booking');
  };

  const handleBookingComplete = async () => {
    setCurrentScreen('appointments');
    loadCustomerAppointments(customer?.token);
  };

  const handleDisconnect = () => {
    clearVerification();
    setPhoneNumber('');
    setCustomer(null);
    setCustomerAppointments([]);
    setCustomerExists(null);
    setUnregisteredBookingEnabled(null);
    setPhoneError('');
    setCurrentScreen('welcome');
  };

  // Render current screen
  if (currentScreen === 'welcome') {
    return (
      <WelcomeScreen
        mainPhoto={mainPhoto}
        businessLogo={businessLogo}
        welcomeText={welcomeText}
        onContinue={handleContinueFromWelcome}
        onShowCancellationPolicy={handleShowCancellationPolicy}
      />
    );
  }

  if (currentScreen === 'cancellationPolicy') {
    return (
      <CancellationPolicy
        onBack={handleBackFromCancellationPolicy}
        businessLogo={businessLogo}
      />
    );
  }

  if (currentScreen === 'phone') {
    return (
      <PhoneInputScreen
        phoneNumber={phoneNumber}
        onChange={handlePhoneChange}
        onSubmit={handlePhoneSubmit}
        error={phoneError}
        isLoading={isCheckingCustomer}
        businessLogo={businessLogo}
      />
    );
  }

  if (currentScreen === 'verification') {
    return (
      <VerificationCodeScreen
        phoneNumber={phoneNumber}
        verificationCode={verificationCode}
        onChange={handleVerificationCodeChange}
        onVerify={handleVerifyCode}
        error={verificationError}
        isLoading={isCheckingCustomer}
        businessLogo={businessLogo}
        getErrorMessage={getVerificationErrorMessage}
      />
    );
  }

  if (currentScreen === 'registration') {
    return (
      <CustomerRegistrationForm
        phone={SKIP_VERIFICATION ? phoneNumber : verifiedPhoneNumber}
        onSubmit={handleRegistrationSubmit}
        isLoading={isCheckingCustomer}
        businessLogo={businessLogo}
      />
    );
  }

  if (currentScreen === 'appointments') {
    return (
      <>
        <AppointmentsList
          appointments={customerAppointments}
          onCancelAppointment={handleCancelAppointment}
          cancellingId={cancellingAppointmentId}
          loading={isLoadingAppointments}
          customer={customer}
          businessLogo={businessLogo}
          businessName={businessContext?.businessName}
          onDisconnect={handleDisconnect}
          onBookNewAppointment={handleBookNewAppointment}
        />
        <CancelConfirmationModal
          isOpen={showCancelModal}
          appointment={appointmentToCancel}
          onConfirm={confirmCancelAppointment}
          onCancel={() => setShowCancelModal(false)}
          isLoading={cancellingAppointmentId === appointmentToCancel?.id}
        />
      </>
    );
  }

  if (currentScreen === 'booking') {
    return (
      <BookingWizard
        customer={customer}
        businessSlug={businessSlug}
        onComplete={handleBookingComplete}
        onDisconnect={handleDisconnect}
      />
    );
  }

  return null;
};

export default CustomerBooking;
